import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ICashflowContext } from '../Interfaces/ICashflowContext';
import { ICashflowData } from '../Interfaces/ICashflowData';
import { ICashflowFilters } from '../Interfaces/ICashflowFilters';
import { ICashflowPaginateData } from '../Interfaces/ICashflowPaginate';
import { cashflowService } from '../services/cashflow';
import { parameterService } from '../services/parameter';
import { PageAction } from '../types/paginateOptions';
import AuthContext from './AuthContext';
import UIContext from './UIContext';

export const CashflowContext = createContext<ICashflowContext>({} as ICashflowContext);

const CashflowContextProvider: React.FC = ({ children }) => {
  const { token, userType, consumerData } = useContext(AuthContext);
  const { setComponentUIState } = useContext(UIContext);

  const isConsumer = userType === "Consumer";
  const clientIdFromContext = consumerData?.id ?? null;

  const [cashflowData, setCashflowData] = useState<ICashflowData[]>([]);
  const [openImagesList, setOpenImagesList] = useState<boolean>(false);
  const [paginateData, setPaginateData] = useState<ICashflowPaginateData>({
    skip: 0,
    currentPage: 1,
    lastPage: null,
    limitePerPage: 10
  })
  const [filter, setFilterStateInternal] = useState<ICashflowFilters>(() => {
    if (isConsumer && clientIdFromContext) {
      return { clientId: clientIdFromContext };
    }
    return {};
  });
  const [invoices, setInvoices] = useState<string[]>([]);
  const [invoiceOptions, setInvoiceOptions] = useState<{ value: string; label: string }[]>([]);
  const [hasFilter, setHasFilter] = useState<boolean>(false);
  const [parametersList, setParametersList] = useState<{ value: string; label: string, classification: string }[]>([]);
  const filterDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const filtersRef = useRef<ICashflowFilters>({});
  const paginateDataRef = useRef<ICashflowPaginateData>({
    skip: 0,
    currentPage: 1,
    lastPage: null,
    limitePerPage: 10
  });
  const filterEffectReadyRef = useRef<boolean>(false);
  type GetCashflowDataFn = (action?: PageAction, paginateContent?: ICashflowPaginateData, appliedFilter?: boolean, preservePagination?: boolean) => Promise<void>;
  const getCashflowDataRef = useRef<GetCashflowDataFn | null>(null);

  const setData = (data: ICashflowData[]) => setCashflowData(data);

  const handleOpenImagesList = (open: boolean) => setOpenImagesList(open);


  function showToast(open: boolean, msg: string, type: "success" | "info" | "warning" | "error") {
    setComponentUIState({
      component: "Toast",
      componentState: { open, message: msg, type }
    })
  }

  async function getInvoices(): Promise<void> {
    try {
      const { status, data: { invoices } } = await cashflowService.getInvoices(token);
      if (status === 200) {
        setInvoices(invoices)
        const options: { value: string; label: string }[] = []
        invoices.map(invoice => options.push({ value: invoice, label: invoice }))
        setInvoiceOptions(options);
      }
    } catch (error) {

    }
  }


  async function getParameters(): Promise<void> {
    try {
      const { status, data: { params } } = await parameterService.findAll(token);
      if (status === 200) {
        const options: { value: string; label: string, classification: string }[] = []
        params.map(({ classification, invoice }) => options.push({ value: invoice, label: invoice, classification }))
        setParametersList(options);
      }
    } catch (error) { }
  }

  const hasActiveFilters = (filters: ICashflowFilters): boolean => {
    return Object.entries(filters).some(([key, value]) => {
      if (isConsumer && key === "clientId") return false;
      if (value === undefined || value === null) return false;
      if (typeof value === "string") return value.trim() !== "";
      return true;
    });
  };

  const enforceClientFilter = useCallback((filters: ICashflowFilters): ICashflowFilters => {
    if (isConsumer) {
      if (clientIdFromContext) {
        return { ...filters, clientId: clientIdFromContext };
      }
      const { clientId, ...rest } = filters;
      return rest;
    }
    return filters;
  }, [clientIdFromContext, isConsumer]);

  const setFilterState = useCallback((nextState: ICashflowFilters | ((prev: ICashflowFilters) => ICashflowFilters)) => {
    if (typeof nextState === "function") {
      setFilterStateInternal(prev => enforceClientFilter(nextState(prev)));
    } else {
      setFilterStateInternal(enforceClientFilter(nextState));
    }
  }, [enforceClientFilter]);

  const setFilters = useCallback((nextFilters: ICashflowFilters) => {
    setFilterState(nextFilters);
  }, [setFilterState]);

  const setFilter = useCallback(<K extends keyof ICashflowFilters>(field: K, value: ICashflowFilters[K]) => {
    if (isConsumer && field === "clientId" && clientIdFromContext && value !== clientIdFromContext) {
      return;
    }

    setFilterState((prev) => {
      const shouldRemove = value === undefined || value === null || (typeof value === "string" && value.trim() === "");

      if (shouldRemove) {
        const { [field]: _, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  }, [clientIdFromContext, isConsumer, setFilterState]);

  const clearFilters = useCallback(() => {
    setFilterState({});
    setHasFilter(false);
  }, [setFilterState]);

  const applyFilters = useCallback((active?: boolean) => {
    const shouldApply = typeof active === "boolean" ? active : hasActiveFilters(filtersRef.current);
    const paginateSnapshot = paginateDataRef.current;
    getCashflowDataRef.current?.("Initial", { ...paginateSnapshot }, shouldApply, true);
  }, []);

  useEffect(() => {
    filtersRef.current = enforceClientFilter(filter);
    if (!filterEffectReadyRef.current) {
      filterEffectReadyRef.current = true;
      return;
    }

    const activeFilters = hasActiveFilters(filter);
    setHasFilter((prev) => (prev === activeFilters ? prev : activeFilters));

    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }

    filterDebounceRef.current = setTimeout(() => {
      getCashflowDataRef.current?.("Initial", { ...paginateDataRef.current }, activeFilters, true);
    }, 300);

    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = null;
      }
    };
  }, [enforceClientFilter, filter]);

  useEffect(() => {
    paginateDataRef.current = paginateData;
  }, [paginateData]);

  useEffect(() => {
    if (isConsumer && clientIdFromContext) {
      setFilterStateInternal(prev => {
        if (prev.clientId === clientIdFromContext) return prev;
        return { ...prev, clientId: clientIdFromContext };
      });
    }
  }, [clientIdFromContext, isConsumer]);

  useEffect(() => {
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
    }
  }, []);

  async function getCashflowData(
    action: PageAction = "Initial",
    paginateContent: ICashflowPaginateData = paginateData,
    appliedFilter = hasFilter,
    preservePagination = false
  ): Promise<void> {
    if (appliedFilter) return getFilteredCashflow(action, paginateContent, preservePagination);

    if (userType === "Consumer") {
      return getConsumerData(action, paginateContent, preservePagination);
    }

    return getAllCashflow(action, paginateContent, preservePagination);
  }

  async function getFilteredCashflow(action: PageAction, paginateContent: ICashflowPaginateData, preservePagination: boolean) {
    const { skip } = paginateContent;
   
    try {
      const { status, data: { totalPages, data } } = await cashflowService.getFilterData(
        skip,
        action,
        filtersRef.current,
        token,
        isConsumer && clientIdFromContext ? { clientId: clientIdFromContext } : undefined
      )

      if (status !== 200) {
        throw "Falha não esperada";
      }

      setData(data)
      setPaginateData((prev) => {
        if (preservePagination && action === "Initial") {
          return {
            ...prev,
            skip: paginateContent.skip,
            currentPage: paginateContent.currentPage,
            lastPage: totalPages ? totalPages : prev.lastPage
          };
        }

        const paginateUpdate = getPaginateData(action, prev);
        return {
          ...prev,
          ...paginateUpdate,
          currentPage: paginateContent.currentPage !== paginateUpdate.currentPage ? paginateContent.currentPage : paginateUpdate.currentPage,
          lastPage: totalPages ? totalPages : prev.lastPage
        };
      })

      !preservePagination && paginateContent.currentPage === 1 && showToast(true, `Filtro aplicado com sucesso!`, "success");
    } catch (error) {
      showToast(true, `${error}`, "error");
    }
  }

  async function getConsumerData(action: PageAction, paginateContent: ICashflowPaginateData, preservePagination: boolean) {
    const { skip } = paginateContent;
    
    try {
      const { status, data: { data, totalPages } } = await cashflowService.getConsumerData(skip, consumerData?.id || 0, action, token);

      if (status !== 200) {
        throw "Falha não esperada";
      }

      setData(data)
      setPaginateData((prev) => {
        if (preservePagination && action === "Initial") {
          return {
            ...prev,
            skip: paginateContent.skip,
            currentPage: paginateContent.currentPage,
            lastPage: totalPages ? totalPages : prev.lastPage,
          };
        }

        const paginateUpdate = getPaginateData(action, prev);
        return {
          ...prev,
          ...paginateUpdate,
          lastPage: totalPages ? totalPages : prev.lastPage,
        };
      })
    } catch (error) {
      showToast(true, `${error}`, "error");
    }
  }

  async function getAllCashflow(action: PageAction, paginateContent: ICashflowPaginateData, preservePagination: boolean) {
    const { skip } = paginateContent;

    try {
      const { status, data: { data, totalPages } } = await cashflowService.getData(skip, action, token);

      if (status !== 200) {
        throw "Falha não esperada";
      }

      setData(data);

      setPaginateData((prev) => {
        if (preservePagination && action === "Initial") {
          return {
            ...prev,
            skip: paginateContent.skip,
            currentPage: paginateContent.currentPage,
            lastPage: totalPages ? totalPages : prev.lastPage,
          };
        }

        const paginateUpdate = getPaginateData(action, prev);
        return {
          ...prev,
          ...paginateUpdate,
          lastPage: totalPages ? totalPages : prev.lastPage,
        };
      })
    } catch (error) {
      showToast(true, `${error}`, "error");
    }
  }

  const getPaginateData = (action: PageAction, currentPaginate: ICashflowPaginateData): { skip: number; currentPage: number } => {
    const { skip, currentPage } = currentPaginate;
    if (action === "Initial") {
      return {
        skip,
        currentPage: 1
      }
    }
    if (action === "Next") {
      return {
        skip: skip + 10,
        currentPage: currentPage + 1
      }
    }

    return {
      skip: skip - 10,
      currentPage: currentPage - 1
    }

  }

  useEffect(() => {
    if (window.location.pathname === '/cashflow') {
      getCashflowData();
    }
  }, [window.location.pathname])

  useEffect(() => {
    getCashflowDataRef.current = getCashflowData;
  });

  return (
    <CashflowContext.Provider value={{
      data: cashflowData,
      setData,
      handleOpenImagesList,
      openImagesList,
      paginateData,
      setPaginateData,
      filter,
      setFilter,
      setFilters,
      clearFilters,
      applyFilters,
      invoiceOptions,
      getInvoices,
      getParameters,
      parametersList,
      hasFilter,
      getCashflowData,
    }} >
      {children}
    </CashflowContext.Provider>
  );
}

export default CashflowContextProvider;
